import { Component, ChangeDetectionStrategy, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'widget-gauge',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="gauge-kapsayici">
      <svg viewBox="0 0 200 120">
        @for (zon of zonlar(); track zon.idx) {
          <path [attr.d]="zon.d" fill="none" [attr.stroke]="zon.renk" stroke-width="14" stroke-linecap="round" opacity="0.25" />
        }
        <path [attr.d]="arkYolu()" fill="none" stroke="rgba(128,128,128,0.12)" stroke-width="14" stroke-linecap="round" />
        <path class="dolu-ark" [attr.d]="doluYol()" fill="none" [attr.stroke]="aktifRenk()" stroke-width="14" stroke-linecap="round" />
        <line class="ibre"
          [attr.x1]="100" [attr.y1]="100"
          [attr.x2]="ibreUc().x" [attr.y2]="ibreUc().y"
          stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
        />
        <circle cx="100" cy="100" r="5" fill="currentColor" />
      </svg>
      <span class="gauge-deger">{{ deger() }}</span>
    </div>
  `,
  styles: [`
    :host { display: block; }
    .gauge-kapsayici { position: relative; text-align: center; }
    svg { width: 100%; max-width: 200px; display: block; margin: 0 auto; }
    .dolu-ark { transition: d 0.5s cubic-bezier(0.4, 0, 0.2, 1), stroke 0.3s; }
    .ibre { transition: x2 0.5s, y2 0.5s; }
    .gauge-deger {
      position: absolute; bottom: 8px; left: 50%; transform: translateX(-50%);
      font-size: 18px; font-weight: 700; opacity: 0.85;
    }
  `],
})
export class GaugeComponent {
  deger = input(0);
  min = input(0);
  max = input(100);
  renkler = input(['#52c41a', '#faad14', '#f5222d']);

  private readonly CX = 100;
  private readonly CY = 100;
  private readonly R = 80;

  private normalDeger = computed(() => {
    const range = this.max() - this.min();
    if (range <= 0) return 0;
    return Math.max(0, Math.min(1, (this.deger() - this.min()) / range));
  });

  aktifRenk = computed(() => {
    const n = this.normalDeger();
    const r = this.renkler();
    if (r.length === 0) return '#1890ff';
    if (n < 1 / 3) return r[0] || '#52c41a';
    if (n < 2 / 3) return r[1] || '#faad14';
    return r[2] || '#f5222d';
  });

  private arkNokta(aci: number) {
    const rad = (aci * Math.PI) / 180;
    return { x: this.CX + this.R * Math.cos(rad), y: this.CY + this.R * Math.sin(rad) };
  }

  private arkYolOlustur(baslangicAci: number, bitisAci: number): string {
    const s = this.arkNokta(baslangicAci);
    const e = this.arkNokta(bitisAci);
    const buyukArc = (bitisAci - baslangicAci) > 180 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${this.R} ${this.R} 0 ${buyukArc} 1 ${e.x} ${e.y}`;
  }

  arkYolu = computed(() => this.arkYolOlustur(180, 360));

  doluYol = computed(() => {
    const n = this.normalDeger();
    if (n <= 0) return '';
    return this.arkYolOlustur(180, 180 + 180 * n);
  });

  zonlar = computed(() => {
    const r = this.renkler();
    const zonSayisi = Math.min(r.length, 3);
    const parcaBoyut = 180 / zonSayisi;
    return r.slice(0, zonSayisi).map((renk, idx) => ({
      idx,
      renk,
      d: this.arkYolOlustur(180 + idx * parcaBoyut, 180 + (idx + 1) * parcaBoyut),
    }));
  });

  ibreUc = computed(() => {
    const aci = 180 + 180 * this.normalDeger();
    const ibreR = this.R - 20;
    const rad = (aci * Math.PI) / 180;
    return { x: this.CX + ibreR * Math.cos(rad), y: this.CY + ibreR * Math.sin(rad) };
  });
}
